/**
 * ScheduleImportCard — get a season's games into Press Box in one go.
 * 2026-10-18. Used by the Press Box setup wizard and by Settings.
 *
 * Three ways in:
 *   • a webcal/iCal link (league sites like MBSportsWeb publish one) — parsed
 *     on the server, instantly;
 *   • an Excel/CSV export — read in the browser (read-excel-file), the rows go
 *     to the stats AI as text;
 *   • a PDF — the server pulls the text out (pdftotext) and the stats AI on
 *     the GPU box reads the schedule out of it.
 * If the GPU is busy (a training run), the import waits in line and the
 * owner gets a notification when the games are ready to confirm.
 *
 * Nothing is created until the coach reviews the list and confirms; past
 * games with a result in the feed arrive as completed games with the score.
 */
import { useEffect, useRef, useState } from 'react';
import { CalendarPlus, Link2, Upload, Loader2, CheckCircle2, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';

const fileText = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file'));
  r.readAsText(file);
});
const fileB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file'));
  r.readAsDataURL(file);
});

/**
 * Excel rows → plain text the model can read (tabs between cells).
 * Same reader + reasons as ImportPlayersPage: read-excel-file/universal
 * (no SheetJS — unpatched advisories), legacy .xls not supported.
 */
async function excelText(file) {
  if (/\.xls$/i.test(file.name || '')) throw new Error('Old Excel (.xls) files are not supported — in Excel, File > Save As > .xlsx (or CSV) and upload that');
  const { readSheet } = await import('read-excel-file/universal');
  const rows = await readSheet(await file.arrayBuffer());
  return (rows || [])
    .map(r => r.map(c => (c == null ? '' : c instanceof Date ? c.toISOString().slice(0, 10) : String(c).trim())).join('\t'))
    .filter(line => line.replace(/\t/g, '').length)
    .join('\n');
}

export default function ScheduleImportCard({ teamId, onConfirmed, title = 'Import the schedule' }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [imp, setImp] = useState(null);        // the current import (view from the server)
  const [games, setGames] = useState([]);      // editable include flags
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const show = (data) => {
    setImp(data);
    setGames((data.games || []).map(g => ({ ...g, include: g.include !== false })));
  };

  // While an import is queued behind the GPU, look in every so often.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (imp?.status === 'queued' && imp.id) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/teams/${teamId}/press-box/schedule/imports/${imp.id}`);
          if (data.status !== 'queued' && data.status !== 'processing') show(data);
        } catch { /* keep waiting */ }
      }, 20000);
    }
    return () => clearInterval(pollRef.current);
  }, [imp?.status, imp?.id, teamId]);          // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/schedule/import`, body, { timeout: 300000 });
      show(data);
      if (data.status === 'queued') toast('The AI’s GPU is busy — your import is in line, we’ll notify you when it’s ready', { icon: '⏳' });
    } catch (e) {
      toast.error(e.response?.data?.error || 'The import failed — try again');
    } finally { setBusy(false); }
  };

  const fromUrl = () => {
    const u = url.trim();
    if (!u) return toast.error('Paste the webcal or iCal link first');
    start({ kind: 'webcal', url: u });
  };

  const fromFile = async (file) => {
    if (!file) return;
    const name = file.name || 'schedule';
    const lower = name.toLowerCase();
    try {
      if (lower.endsWith('.ics')) return await start({ kind: 'webcal', text: await fileText(file), filename: name });
      if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return await start({ kind: 'document', text: await excelText(file), filename: name });
      if (lower.endsWith('.csv') || lower.endsWith('.txt')) return await start({ kind: 'document', text: await fileText(file), filename: name });
      if (lower.endsWith('.pdf')) {
        if (file.size > 14 * 1024 * 1024) return toast.error('The PDF must be 14 MB or smaller');
        return await start({ kind: 'document', data: await fileB64(file), mimeType: 'application/pdf', filename: name });
      }
      toast.error('Use a .ics, .pdf, .xlsx or .csv file');
    } catch (e) {
      toast.error(e.message || 'Could not read the file');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/schedule/imports/${imp.id}/confirm`, { games });
      toast.success(`${data.created} game${data.created === 1 ? '' : 's'} added${data.skipped ? ` (${data.skipped} already on the schedule)` : ''}`);
      setImp(null); setGames([]); setUrl('');
      onConfirmed?.(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not create the games');
    } finally { setBusy(false); }
  };

  const toggle = (i) => setGames((g) => g.map((x, j) => (j === i ? { ...x, include: !x.include } : x)));
  const included = games.filter(g => g.include).length;
  const box = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 };

  return (
    <div className="card mb-4" data-schedule-import>
      <div className="flex items-center gap-2 font-bold mb-2"><CalendarPlus size={16} style={{ color: 'var(--ice)' }} /> {title}</div>
      {!imp && (
        <>
          <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
            Paste your league&rsquo;s calendar link (webcal/iCal — most league sites have one under &ldquo;subscribe&rdquo;),
            or upload the schedule as a PDF, Excel or .ics file and the stats AI will pull the games out.
          </div>
          <input className="input-field mb-2" style={{ width: '100%', padding: '10px 12px', fontSize: 13 }}
            placeholder="webcal://league.example.com/calendar.ashx?IDs=…"
            value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fromUrl()} />
          <div className="flex items-center gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="btn-ice" onClick={fromUrl} disabled={busy}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Fetch the schedule
            </button>
            <span className="text-sm" style={{ color: 'var(--text-3)' }}>or</span>
            <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
              <Upload size={14} /> Upload a file (PDF, Excel, .ics, CSV)
            </button>
            <input ref={fileRef} type="file" accept=".ics,.pdf,.xlsx,.xls,.csv,.txt" style={{ display: 'none' }}
              onChange={(e) => fromFile(e.target.files?.[0])} />
          </div>
        </>
      )}

      {imp?.status === 'queued' && (
        <div style={box} className="text-sm">
          <div className="flex items-center gap-2"><Clock size={15} style={{ color: 'var(--puck)' }} />
            <strong>In line behind the GPU.</strong></div>
          <div className="mt-1" style={{ color: 'var(--text-2)' }}>
            The stats AI&rsquo;s card is busy right now. Your schedule is queued and will be read as soon as it frees up —
            you&rsquo;ll get a notification here when the games are ready to confirm. You can keep going; this doesn&rsquo;t block anything.
          </div>
          <div className="mt-2"><button type="button" className="btn-ghost" style={{ padding: '5px 10px' }} onClick={() => setImp(null)}>Start over</button></div>
        </div>
      )}

      {imp?.status === 'failed' && (
        <div style={box} className="text-sm">
          <div style={{ color: 'var(--penalty)' }}>{imp.error || 'The import failed.'}</div>
          <div className="mt-2"><button type="button" className="btn-ghost" style={{ padding: '5px 10px' }} onClick={() => setImp(null)}>Try again</button></div>
        </div>
      )}

      {imp?.status === 'ready' && (
        <div>
          <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
            <CheckCircle2 size={14} style={{ color: 'var(--goal)', verticalAlign: '-2px' }} />{' '}
            Found <strong>{games.length}</strong> game{games.length === 1 ? '' : 's'}
            {imp.teamName ? <> in <strong>{imp.teamName}</strong></> : null}
            {imp.skipped?.length ? <> ({imp.skipped.length} non-game entries like practices were left out)</> : null}.
            Untick any you don&rsquo;t want, then confirm.
          </div>
          <div style={{ ...box, maxHeight: 320, overflowY: 'auto', padding: 0 }}>
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <tbody>
                {games.map((g, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)', opacity: g.include ? 1 : 0.45 }}>
                    <td style={{ padding: '6px 8px', width: 28 }}>
                      <input type="checkbox" checked={g.include} onChange={() => toggle(i)} aria-label="Include game" />
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{g.date}{g.time ? ` ${g.time}` : ''}</td>
                    <td style={{ padding: '6px 8px' }}>{g.homeAway === 'away' ? '@ ' : 'vs '}{g.opponent || 'TBA'}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 700 }}>
                      {g.result
                        ? (typeof g.result === 'object'
                          ? `${g.result.us > g.result.them ? 'W' : g.result.us < g.result.them ? 'L' : 'T'} ${g.result.us}–${g.result.them}`
                          : `${g.result}${Array.isArray(g.score) ? ` ${g.score[0]}–${g.score[1]}` : ''}`)
                        : ''}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-3)' }}>{g.location || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button type="button" className="btn-ice" onClick={confirm} disabled={busy || !included}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Add {included} game{included === 1 ? '' : 's'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setImp(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
